import { enCA, enUS, frCA, Locale as DateFnsLocale } from 'date-fns/locale'

import { TAbsoluteLocale } from '@src/config/localisation/locale-config.types'

export interface DateFormatConfig {
  options: Intl.DateTimeFormatOptions
}

/**
 * Base format configurations that work across locales.
 * These use Intl.DateTimeFormatOptions to ensure proper localization.
 */
export const BASE_DATE_FORMATS = {
  // Numeric date with slashes: "01/08/2026" (en-US). Equivalent to date-fns 'P'
  NUMERIC_SLASH_DATE: {
    options: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    },
  },

  // Abbreviated month, day, year: "Jan 08, 2026". Equivalent to date-fns 'MMM dd, yyyy'
  SHORT_MONTH_DAY_YEAR: {
    options: {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    },
  },
  // Abbreviated month, day, and time: "Dec 20, 8:53 AM". Equivalent to date-fns 'MMM dd, h:mm a'
  SHORT_MONTH_DAY_AND_TIME_12H: {
    options: {
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    },
  },

  // Full month and year only: "January 2026". Equivalent to date-fns 'MMMM yyyy'
  MONTH_YEAR: {
    options: {
      month: 'long',
      year: 'numeric',
    },
  },
  // Full month, day, year: "January 8, 2026". Equivalent to date-fns 'MMMM dd, yyyy'
  MONTH_SHORT_DAY_YEAR: {
    options: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
  },
} as const satisfies Record<string, DateFormatConfig>

/**
 * Semantic format names for date formatting.
 * Derived from BASE_DATE_FORMATS keys to ensure type safety.
 */
export type DateFormatName = keyof typeof BASE_DATE_FORMATS

/**
 * Override using date-fns with a custom format string.
 */
export interface DateFnsOverrideConfig {
  type: 'date-fns'
  /** The date-fns format string to use (e.g., 'PPP', 'P', 'MMM dd') */
  formatString: string
  /** Optional locale override — defaults to the current app locale */
  locale?: TAbsoluteLocale
}

/**
 * Override using Intl.DateTimeFormat but with a different locale.
 * Useful when you want fr-ca or en-ca to render the same as en-us.
 */
export interface IntlOverrideConfig {
  type: 'intl'
  /** The locale to use for Intl.DateTimeFormat formatting */
  locale: TAbsoluteLocale
}

/**
 * A format override can either swap the formatter to date-fns
 * or redirect to a different locale via Intl.
 */
export type DateFormatOverrideConfig = DateFnsOverrideConfig | IntlOverrideConfig

/**
 * Override configuration type: maps locale -> format name -> override config.
 * Use this to specify custom formatting for specific locale + format combinations.
 */
export type DateFormatOverrides = Partial<
  Record<TAbsoluteLocale, Partial<Record<DateFormatName, DateFormatOverrideConfig>>>
>

/**
 * Options for the useFormattedDate hook
 */
export interface UseFormattedDateOptions {
  /**
   * Custom date-fns overrides for specific locale + format combinations.
   * Uses default overrides if not provided.
   */
  overrides?: DateFormatOverrides
}

/**
 * Return type for the useFormattedDate hook
 */
export interface FormattedDateResult {
  /**
   * The formatted date string for display
   */
  formatted: string

  /**
   * The ISO 8601 datetime string for the datetime attribute
   */
  iso: string
}

/**
 * Format names that include time component.
 * Used to determine whether datetime attribute should include time.
 */
export const TIME_FORMATS: ReadonlySet<DateFormatName> = new Set(['SHORT_MONTH_DAY_AND_TIME_12H'])

/**
 * Maps app locale to date-fns locale object
 */
export const DATE_FNS_LOCALES: Record<string, DateFnsLocale> = {
  'en-us': enUS,
  'en-ca': enCA,
  'fr-ca': frCA,
}

/**
 * Get the date-fns locale object for the given app locale
 */
export function getDateFnsLocale(locale: TAbsoluteLocale): DateFnsLocale {
  const normalizedLocale = locale.toLowerCase()
  return DATE_FNS_LOCALES[normalizedLocale] ?? enUS
}

export const STANDARD_DATE_OVERRIDES = {
  REVIEW_ITEM: {
    'en-ca': {
      NUMERIC_SLASH_DATE: {
        type: 'intl',
        locale: 'en-us',
      },
    },
    'fr-ca': {
      NUMERIC_SLASH_DATE: {
        type: 'intl',
        locale: 'en-us',
      },
    },
  },
  STORE_CREDITS: {
    'en-ca': {
      SHORT_MONTH_DAY_YEAR: {
        type: 'intl',
        locale: 'en-us',
      },
    },
    'fr-ca': {
      SHORT_MONTH_DAY_YEAR: {
        type: 'intl',
        locale: 'en-us',
      },
    },
  },
  INFLUENCER_IRL: {
    'en-us': {
      MONTH_SHORT_DAY_YEAR: {
        type: 'date-fns',
        formatString: 'PPP',
      },
    },
    'en-ca': {
      MONTH_SHORT_DAY_YEAR: {
        type: 'date-fns',
        formatString: 'PPP',
        locale: 'en-us',
      },
    },
    'fr-ca': {
      MONTH_SHORT_DAY_YEAR: {
        type: 'date-fns',
        formatString: 'PPP',
        locale: 'en-us',
      },
    },
  },
} as const satisfies Record<string, DateFormatOverrides>
